import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import CreateWedding from "@/pages/CreateWedding";
import Admin from "@/pages/Admin";
import Rsvp from "@/pages/Rsvp";
import Blog from "@/pages/Blog";
import Article from "@/pages/Article";
import Studio from "@/pages/Studio";
import Support from "@/pages/Support";
import { useSite } from "@/hooks/useSite";
import { trackPage } from "@/lib/track";

/** Record each page, and run whatever analytics script the owner has configured.

    Both live inside the router because both need to know when the route
    changes — a single-page app never reloads, so nothing else would notice. */
function SiteScripts() {
  const location = useLocation();
  const site = useSite();
  const snippet = site?.analytics?.snippet || "";

  useEffect(() => {
    trackPage(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!snippet) return undefined;
    const host = document.createElement("div");
    host.style.display = "none";
    const fragment = document.createRange().createContextualFragment(snippet);
    // a fragment's <script> nodes are inert; recreating them is what runs them
    fragment.querySelectorAll("script").forEach((original) => {
      const script = document.createElement("script");
      Array.from(original.attributes).forEach((attr) =>
        script.setAttribute(attr.name, attr.value)
      );
      script.text = original.text;
      original.replaceWith(script);
    });
    host.appendChild(fragment);
    document.body.appendChild(host);
    return () => host.remove();
  }, [snippet]);

  return null;
}

function App() {
  const [lang, setLang] = useState(() => document.documentElement.lang);

  useEffect(() => {
    const sync = () => setLang(document.documentElement.lang);
    window.addEventListener("languagechange", sync);
    return () => window.removeEventListener("languagechange", sync);
  }, []);

  return (
    <div className="App" key={lang}>
      <BrowserRouter>
        <SiteScripts />
        <Routes>
          <Route path="/" element={<Landing />} />
          {/* a shareable link that opens the demonstration straight away */}
          <Route path="/demo" element={<Navigate to="/?demo=1" replace />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Article />} />
          <Route path="/support" element={<Support />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/create" element={<CreateWedding />} />
          <Route path="/w/:weddingId/admin" element={<Admin />} />
          <Route path="/w/:weddingId/rsvp" element={<Rsvp />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
